import { refreshTokensSingleton } from '@services/refresh.service';

const SESSION_EXPIRED_FLAG = 'code360_session_expired';

let hardResetInProgress = false;

export function getSessionExpiredFlagKey() {
    return SESSION_EXPIRED_FLAG;
}

/**
 * App-wide session refresh — always uses refreshTokensSingleton (same lock as axios interceptor).
 */
export async function refreshAuthSingleFlight(opts = {}) {
    try {
        const { useAuthStore } = await import('@stores/auth.store');
        return await refreshTokensSingleton(
            () => useAuthStore.getState().refreshToken,
            useAuthStore.setState,
            () => hardSessionExpiredHardReset(),
            { skipLogoutOnError: opts.skipLogoutOnError ?? false },
        );
    } catch (err) {
        const status = err?.status ?? err?.response?.status;
        const isAuth = status === 400 || status === 401 || status === 403;
        if (isAuth) {
            return null;
        }
        throw err;
    }
}

async function clearBrowserCachesBestEffort() {
    try {
        if (typeof caches !== 'undefined' && caches?.keys) {
            const keys = await caches.keys();
            await Promise.allSettled(keys.map((k) => caches.delete(k)));
        }
    } catch {
        // ignore
    }

    try {
        if (indexedDB && indexedDB.databases) {
            const dbs = await indexedDB.databases();
            await Promise.allSettled(
                (dbs || []).map((db) => {
                    if (!db?.name) return Promise.resolve();
                    return new Promise((resolve) => {
                        const req = indexedDB.deleteDatabase(db.name);
                        req.onsuccess = () => resolve();
                        req.onerror = () => resolve();
                        req.onblocked = () => resolve();
                    });
                }),
            );
        }
    } catch {
        // ignore
    }

    try {
        if (navigator?.serviceWorker?.getRegistrations) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.allSettled(regs.map((r) => r.unregister()));
        }
    } catch {
        // ignore
    }
}

export async function hardSessionExpiredHardReset() {
    if (hardResetInProgress) return;
    hardResetInProgress = true;

    try {
        sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1');
    } catch {
        // ignore
    }

    try {
        const { useAuthStore } = await import('@stores/auth.store');
        await Promise.resolve(useAuthStore.getState().hardLogout?.() ?? useAuthStore.getState().logout?.());
    } catch {
        // ignore
    }

    try {
        localStorage.clear();
    } catch {
        // ignore
    }
    try {
        const flag = sessionStorage.getItem(SESSION_EXPIRED_FLAG);
        sessionStorage.clear();
        if (flag) sessionStorage.setItem(SESSION_EXPIRED_FLAG, flag);
    } catch {
        // ignore
    }

    await clearBrowserCachesBestEffort();

    try {
        window.location.replace('/sign-in');
        window.location.reload();
    } catch {
        // ignore
    } finally {
        hardResetInProgress = false;
    }
}
